import { z } from 'zod';
import {
  Currency,
  FinancingType,
  PurchaseHorizon,
  PurchasePurpose,
  QuoteStatus,
  UnitCommercialStatus,
  UnitType,
} from '../enums';
import type { BuyerProfile, SeparationRow, SeparationsSummary } from '../funnel/commercial';
import { cursorPaginationSchema, dateBoundSchema, optionalPatchText } from './common';
import { nextStepSchema } from './interactions';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));
const money = z.coerce.number().min(0).max(99_999_999_999);

/**
 * Fecha mínima razonable de una corrección comercial (Lote 5 · H4, revisión): ningún hecho comercial del CRM es
 * anterior al año 2000, así que una fecha más vieja es un error de tipeo (o un `null` coaccionado: con
 * `z.coerce.date()` un null se volvía 1970-01-01 y pasaba en silencio).
 */
export const COMMERCIAL_MIN_DATE = new Date('2000-01-01T00:00:00.000Z');
const invalidDate = { errorMap: () => ({ message: 'Indica una fecha válida' }) };
/** Fecha de una corrección: solo un instante ISO o una fecha (nunca null, true ni un número), desde el 2000. */
const correctionDate = z
  .union([z.string(), z.date()], invalidDate)
  .pipe(
    z.coerce
      .date(invalidDate)
      .min(COMMERCIAL_MIN_DATE, 'La fecha no puede ser anterior al año 2000'),
  );

// ---------------------------------------------------------------- perfil de compra, atribución y consentimiento

export const purchaseProfileSchema = z
  .object({
    budgetMin: money.optional().nullable(),
    budgetMax: money.optional().nullable(),
    budgetCurrency: z.nativeEnum(Currency).optional(),
    bedroomsWanted: z.coerce.number().int().min(0).max(20).optional().nullable(),
    /** No enviado = se conserva (el bot guarda el perfil por partes); vacío o null = se borra. */
    typologyWanted: optionalPatchText(40),
    financingWanted: z.nativeEnum(FinancingType).optional().nullable(),
    purchaseHorizon: z.nativeEnum(PurchaseHorizon).optional().nullable(),
    purchasePurpose: z.nativeEnum(PurchasePurpose).optional().nullable(),
  })
  .refine(
    (p) =>
      p.budgetMin === null ||
      p.budgetMin === undefined ||
      p.budgetMax === null ||
      p.budgetMax === undefined ||
      p.budgetMin <= p.budgetMax,
    { message: 'El presupuesto mínimo no puede superar al máximo', path: ['budgetMax'] },
  );
export type PurchaseProfileInput = z.infer<typeof purchaseProfileSchema>;
export type PurchaseProfilePayload = z.input<typeof purchaseProfileSchema>;

export interface PurchaseProfile {
  budgetMin: number | null;
  budgetMax: number | null;
  budgetCurrency: Currency;
  bedroomsWanted: number | null;
  typologyWanted: string | null;
  financingWanted: FinancingType | null;
  purchaseHorizon: PurchaseHorizon | null;
  purchasePurpose: PurchasePurpose | null;
  updatedAt: string | null;
}

/** Atribución estructurada de la captación (ids de la plataforma publicitaria y origen web). */
export const leadAttributionSchema = z.object({
  platform: optionalText(40),
  campaignId: optionalText(80),
  campaignName: optionalText(200),
  adsetId: optionalText(80),
  adsetName: optionalText(200),
  adId: optionalText(80),
  adName: optionalText(200),
  formId: optionalText(80),
  formName: optionalText(200),
  pageId: optionalText(80),
  sourceUrl: optionalText(1000),
  referrer: optionalText(1000),
});
export type LeadAttribution = z.infer<typeof leadAttributionSchema>;
export type LeadAttributionPayload = z.input<typeof leadAttributionSchema>;

export const consentInputSchema = z
  .object({
    /** true = dio consentimiento; false = pidió no ser contactado (opt-out). */
    granted: z.boolean(),
    source: optionalText(60),
    /**
     * Motivo: obligatorio al marcar «no contactar» (quién lo pidió y cómo). Al levantar un opt-out desde la ficha
     * también es obligatorio (lo exige la API): levantar un «no contactar» es una acción humana con motivo.
     */
    reason: optionalText(300),
  })
  .refine((v) => v.granted || !!v.reason, {
    path: ['reason'],
    message: 'Indica el motivo del «no contactar»',
  });
export type ConsentInput = z.infer<typeof consentInputSchema>;
export type ConsentInputPayload = z.input<typeof consentInputSchema>;

export interface ContactConsent {
  consentAt: string | null;
  consentSource: string | null;
  optOutAt: string | null;
  optOutReason: string | null;
  /** false cuando hay opt-out vigente: no se debe escribir ni llamar. */
  contactable: boolean;
}

// ---------------------------------------------------------------- proformas

export const createQuoteSchema = z
  .object({
    unitId: z.string().uuid().optional().nullable(),
    /** Precio de lista; si hay unidad y no se envía, se toma el de la unidad. */
    priceList: money.optional().nullable(),
    discount: money.default(0),
    /** Precio final; si no se envía = lista − descuento. */
    price: money.optional().nullable(),
    /** Sin valor = moneda por defecto de la empresa (o la de la unidad). */
    currency: z.nativeEnum(Currency).optional().nullable(),
    financingType: z.nativeEnum(FinancingType).optional().nullable(),
    expiresAt: z.coerce.date().optional().nullable(),
    notes: optionalText(1000),
    /** Fecha de la visita/presentación asociada; si se envía, la proforma cuenta como visita válida. */
    visitAt: z.coerce.date().optional().nullable(),
    next: nextStepSchema.optional(),
  })
  .refine((q) => q.unitId || q.priceList || q.price, {
    message: 'Indica una unidad o un precio',
    path: ['price'],
  });
export type CreateQuoteInput = z.infer<typeof createQuoteSchema>;
export type CreateQuotePayload = z.input<typeof createQuoteSchema>;

export const annulQuoteSchema = z.object({
  reason: z.string().trim().min(2, 'Indica el motivo').max(300),
  next: nextStepSchema.optional(),
});
export type AnnulQuoteInput = z.infer<typeof annulQuoteSchema>;

/**
 * Corrección de una proforma vigente o expirada (Lote 5 · H4, P14, ux-comercial-04): precio de lista, descuento,
 * precio final, vigencia y notas. La unidad y la moneda no se corrigen (se anula y se emite otra). Lo que no se
 * envía se conserva; `notes` vacío o null borra. Sin `price`, el precio final vuelve a ser lista − descuento.
 * `expiresAt: null` deja la proforma **sin vencimiento** (queda VIGENTE hasta que se anule o convierta); una fecha
 * debe ser válida y desde el año 2000. Un campo que no está aquí responde 400 (no se ignora en silencio).
 */
export const updateQuoteSchema = z
  .object({
    priceList: money.optional().nullable(),
    discount: money.optional(),
    price: money.optional(),
    expiresAt: correctionDate.optional().nullable(),
    notes: optionalPatchText(1000),
  })
  .strict('Solo se pueden corregir precio de lista, descuento, precio, vigencia y notas')
  .refine((q) => Object.values(q).some((v) => v !== undefined), {
    message: 'No hay nada que corregir',
  });
export type UpdateQuoteInput = z.infer<typeof updateQuoteSchema>;
export type UpdateQuotePayload = z.input<typeof updateQuoteSchema>;

export const quoteFiltersSchema = cursorPaginationSchema.extend({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  leadId: z.string().uuid().optional(),
  advisorId: z.string().uuid().optional(),
  status: z.nativeEnum(QuoteStatus).optional(),
  q: z.string().trim().min(2).max(100).optional(),
  /** Rango sobre la fecha de emisión: fechas puras (días completos en la zona de la empresa) o instantes. */
  from: dateBoundSchema.optional(),
  to: dateBoundSchema.optional(),
});
export type QuoteFilters = z.infer<typeof quoteFiltersSchema>;

export interface QuoteDto {
  id: string;
  code: string;
  companyId: string;
  projectId: string;
  projectName: string;
  leadId: string;
  contactName: string;
  advisorName: string | null;
  unitId: string | null;
  unitCode: string | null;
  unitType: UnitType | null;
  priceList: string | null;
  discount: string;
  price: string;
  currency: Currency;
  financingType: FinancingType | null;
  status: QuoteStatus;
  expiresAt: string | null;
  notes: string | null;
  annulledAt: string | null;
  annulReason: string | null;
  convertedAt: string | null;
  createdByName: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------- procesos

export const refundDealSchema = z.object({
  refundedAt: z.coerce.date(),
  /** Código de catalog_items(kind = DROP_REASON). Los técnicos (ERROR_DATA…) se usan en la anulación, no aquí. */
  dropReason: z.string().min(1, 'Selecciona el motivo').max(40),
  notes: optionalText(1000),
  /** Si el lead sigue activo tras la devolución, debe quedar con próximo paso; si no, desistir. */
  next: nextStepSchema.optional(),
});
export type RefundDealInput = z.infer<typeof refundDealSchema>;
export type RefundDealPayload = z.input<typeof refundDealSchema>;

/** Anular borra el proceso del historial: el motivo debe explicarse (Lote 5 · H1, ux-comercial-12). */
export const ANNUL_DEAL_REASON_MIN = 10;

export const annulDealSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(ANNUL_DEAL_REASON_MIN, `Explica el motivo (al menos ${ANNUL_DEAL_REASON_MIN} caracteres)`)
    .max(300),
  /** Código técnico opcional (ERROR_DATA, ERROR_REFINANCIAMIENTO). */
  dropReason: optionalText(40),
  next: nextStepSchema.optional(),
});
export type AnnulDealInput = z.infer<typeof annulDealSchema>;

/**
 * Corrección de una separación o venta ya registrada (Lote 5 · H4, P14, ux-comercial-04), para quien gestiona la
 * empresa: N° de contrato, banco, modalidad, fechas (separación: `startedAt`; venta: `finishedAt` y
 * `minuteSignedAt`), montos (`amountInitial`, en su propia moneda `initialCurrency`; `amountTotal` solo en ventas:
 * el de una separación es la suma de los precios pactados por unidad, H2 · P15, y responde
 * `400 SEPARATION_TOTAL_DERIVED`) y las notas de la devolución si la hubo. Las unidades y las monedas no se
 * corrigen: se anula el proceso y se registra de nuevo. Lo que no se envía se conserva; un texto vacío o null borra. Las fechas pasan por las mismas reglas que al registrar (nunca futuras, la
 * venta no antes de su separación, la devolución no antes del proceso); la de separación y la de venta no admiten
 * null (el hecho siempre tiene fecha) ni fechas anteriores al año 2000; `minuteSignedAt: null` borra la fecha de la
 * minuta. Un campo que no está aquí responde 400 (no se ignora en silencio).
 */
export const updateDealSchema = z
  .object({
    contractNumber: optionalPatchText(60),
    bank: optionalPatchText(80),
    contractMode: z.nativeEnum(FinancingType).optional().nullable(),
    startedAt: correctionDate.optional(),
    finishedAt: correctionDate.optional(),
    minuteSignedAt: correctionDate.optional().nullable(),
    amountTotal: money.optional().nullable(),
    amountInitial: money.optional().nullable(),
    refundNotes: optionalPatchText(1000),
  })
  .strict(
    'Solo se pueden corregir N° de contrato, banco, modalidad, fechas, montos y notas de la devolución; las unidades no',
  )
  .refine((d) => Object.values(d).some((v) => v !== undefined), {
    message: 'No hay nada que corregir',
  });
export type UpdateDealInput = z.infer<typeof updateDealSchema>;
export type UpdateDealPayload = z.input<typeof updateDealSchema>;

export const dealFiltersSchema = cursorPaginationSchema.extend({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  leadId: z.string().uuid().optional(),
  advisorId: z.string().uuid().optional(),
  type: z.enum(['SEPARACION', 'VENTA']).optional(),
  /** ACTIVO = sin devolución ni anulación; DEVUELTO; ANULADO */
  state: z.enum(['ACTIVO', 'DEVUELTO', 'ANULADO']).optional(),
  q: z.string().trim().min(2).max(100).optional(),
  /**
   * Rango de fechas (fechas puras = días completos en la zona de la empresa, o instantes) sobre la fecha del
   * proceso según su tipo: separación → fecha de separación, venta → fecha de venta. Sin `type`, cada proceso
   * se filtra por la suya (una venta sin separación previa no se esconde).
   */
  from: dateBoundSchema.optional(),
  to: dateBoundSchema.optional(),
});
export type DealFilters = z.infer<typeof dealFiltersSchema>;

export interface DealDto {
  id: string;
  companyId: string;
  projectId: string;
  projectName: string;
  leadId: string;
  contactName: string;
  advisorName: string | null;
  type: 'SEPARACION' | 'VENTA';
  quoteId: string | null;
  quoteCode: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  minuteSignedAt: string | null;
  refundedAt: string | null;
  dropReason: string | null;
  refundNotes: string | null;
  annulledAt: string | null;
  annulReason: string | null;
  contractMode: FinancingType | null;
  bank: string | null;
  /** Moneda del precio: la de la unidad (Lote 5 · H2, P9). */
  currency: Currency;
  amountTotal: string | null;
  amountInitial: string | null;
  /** Moneda propia de la reserva / inicial (`amountInitial`). */
  initialCurrency: Currency;
  contractNumber: string | null;
  units: Array<{ id: string; code: string; unitType: UnitType; price: string | null }>;
  state: 'ACTIVO' | 'DEVUELTO' | 'ANULADO';
  createdAt: string;
}

// ---------------------------------------------------------------- stock de unidades

/** Tamaño de página del stock (por defecto y máximo) y tope de unidades pedidas por id. */
export const UNITS_PAGE_DEFAULT = 200;
export const UNITS_PAGE_MAX = 500;
export const UNIT_IDS_MAX = 50;

export const unitFiltersSchema = cursorPaginationSchema.extend({
  projectId: z.string().uuid(),
  status: z.nativeEnum(UnitCommercialStatus).optional(),
  unitType: z.nativeEnum(UnitType).optional(),
  typology: z.string().trim().max(40).optional(),
  subdivisionId: z.string().uuid().optional(),
  /** Solo unidades proformables (disponibles y fuera de blacklist). */
  available: z.coerce.boolean().optional(),
  /** Búsqueda en el servidor por código o tipología (contiene, sin distinguir mayúsculas). */
  q: z.string().trim().max(40).optional(),
  /**
   * Unidades pedidas por id (hasta `UNIT_IDS_MAX`), aunque no cumplan los demás filtros ni estén disponibles:
   * el selector las usa para mostrar la unidad ya separada por el lead o las ya elegidas.
   */
  ids: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v, ctx) => {
      if (v === undefined) return undefined;
      const list = [...new Set((Array.isArray(v) ? v : v.split(',')).map((x) => x.trim()))].filter(
        Boolean,
      );
      if (list.length > UNIT_IDS_MAX) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Máximo ${UNIT_IDS_MAX} unidades` });
        return z.NEVER;
      }
      if (!list.every((x) => z.string().uuid().safeParse(x).success)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Id de unidad inválido' });
        return z.NEVER;
      }
      return list.length ? list : undefined;
    }),
  /** Filtros de stock: dormitorios exactos y rangos de área total (m²) y precio vigente (venta o lista). */
  bedrooms: z.coerce.number().int().min(0).max(20).optional(),
  areaMin: z.coerce.number().min(0).optional(),
  areaMax: z.coerce.number().min(0).optional(),
  priceMin: z.coerce.number().min(0).optional(),
  priceMax: z.coerce.number().min(0).optional(),
  /**
   * Página de `limit` unidades (200 por defecto) en el orden del stock (torre, piso, código); `nextCursor`
   * continúa la lista y `total` llega en la primera página («Mostrando 200 de N»).
   */
  limit: z.coerce.number().int().min(1).max(UNITS_PAGE_MAX).default(UNITS_PAGE_DEFAULT),
});
export type UnitFilters = z.infer<typeof unitFiltersSchema>;

export const unitInputSchema = z.object({
  code: z.string().trim().min(1, 'Ingresa el código').max(30),
  subdivisionId: z.string().uuid().optional().nullable(),
  unitType: z.nativeEnum(UnitType),
  typology: optionalText(40),
  floor: z.coerce.number().int().min(-10).max(200).optional().nullable(),
  view: optionalText(60),
  areaTotal: z.coerce.number().min(0).optional().nullable(),
  areaBuilt: z.coerce.number().min(0).optional().nullable(),
  areaFree: z.coerce.number().min(0).optional().nullable(),
  bedrooms: z.coerce.number().int().min(0).max(20).optional().nullable(),
  bathrooms: z.coerce.number().min(0).max(20).optional().nullable(),
  priceList: money.optional().nullable(),
  priceSale: money.optional().nullable(),
  currency: z.nativeEnum(Currency).default(Currency.USD),
  externalIds: z.record(z.unknown()).optional(),
});
export type UnitInput = z.infer<typeof unitInputSchema>;
export type UnitPayload = z.input<typeof unitInputSchema>;

export const createUnitSchema = unitInputSchema.extend({ projectId: z.string().uuid() });
export const updateUnitSchema = unitInputSchema.partial();
export type UpdateUnitInput = z.infer<typeof updateUnitSchema>;

export const blockUnitSchema = z.object({
  blocked: z.boolean(),
  reason: optionalText(300),
});
export type BlockUnitInput = z.infer<typeof blockUnitSchema>;

/**
 * Carga masiva de unidades (Excel o CSV convertido a texto en el navegador): columnas codigo, tipo, tipologia,
 * piso, dormitorios, area, precio_lista, precio, moneda, torre (o sus equivalentes en inglés).
 */
/** Tamaño máximo del texto de una carga de unidades (unos 5 MB: decenas de miles de filas). */
export const UNITS_IMPORT_MAX_CHARS = 5_000_000;
/**
 * Moneda con la que nace una unidad cuando ni la celda del precio, ni la columna Moneda ni la cabecera la dicen
 * (la misma que el valor por defecto de `units.currency`); la vista previa la muestra como moneda efectiva.
 */
export const UNIT_DEFAULT_CURRENCY: Currency = 'USD';

export const unitsImportSchema = z.object({
  projectId: z.string().uuid(),
  csv: z
    .string()
    .min(1)
    .max(
      UNITS_IMPORT_MAX_CHARS,
      'El archivo es demasiado grande (más de 5 millones de caracteres): divídelo en varios',
    ),
  /** Actualiza las unidades existentes (por código); apagado por defecto: si es false, se omiten. */
  updateExisting: z.boolean().default(false),
  /**
   * Confirmación expresa cuando la carga cambia precios (lista o venta) o la moneda de unidades existentes: sin
   * ella la API responde 400 con cuántas cambiarían. La vista previa (`POST /units/import/preview`) muestra el
   * antes y después.
   */
  confirmPriceChanges: z.boolean().default(false),
  /**
   * Separador decimal del archivo cuando se conoce. El navegador manda «.» para un Excel: sus celdas numéricas se
   * serializan siempre con punto, así «45.125» son 45,125 m² y no 45 125. Sin pista, un CSV con «;» se lee con
   * coma decimal y uno con «,» con la regla de miles («1,250» = 1250).
   */
  decimal: z.enum([',', '.']).optional(),
});
export type UnitsImportInput = z.infer<typeof unitsImportSchema>;
export type UnitsImportPayload = z.input<typeof unitsImportSchema>;

/** Columnas que la carga de unidades sabe leer (clave interna → nombre para la gente). */
export const UNIT_IMPORT_FIELD_LABELS = {
  unitType: 'Tipo',
  typology: 'Tipología',
  floor: 'Piso',
  view: 'Vista',
  bedrooms: 'Dormitorios',
  bathrooms: 'Baños',
  areaTotal: 'Área total',
  areaBuilt: 'Área techada',
  areaFree: 'Área libre',
  priceList: 'Precio de lista',
  priceSale: 'Precio de venta',
  currency: 'Moneda',
  subdivision: 'Torre / etapa',
} as const;
export type UnitImportField = keyof typeof UNIT_IMPORT_FIELD_LABELS;
export const UNIT_IMPORT_FIELDS = Object.keys(UNIT_IMPORT_FIELD_LABELS) as UnitImportField[];
/** Columnas numéricas (pasan por `parseNumber`; un texto que no es número deja la fila en error). */
export const UNIT_IMPORT_NUMERIC_FIELDS: ReadonlyArray<UnitImportField> = [
  'floor',
  'bedrooms',
  'bathrooms',
  'areaTotal',
  'areaBuilt',
  'areaFree',
  'priceList',
  'priceSale',
];

/** Valores de una fila tal como se van a guardar (solo las columnas presentes en el archivo). */
export interface UnitsImportValues {
  unitType?: UnitType;
  typology?: string | null;
  floor?: number | null;
  view?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  areaTotal?: number | null;
  areaBuilt?: number | null;
  areaFree?: number | null;
  priceList?: number | null;
  priceSale?: number | null;
  currency?: Currency;
  /** Nombre de la torre / etapa (se crea si no existe). */
  subdivision?: string | null;
}

/** SIN_CAMBIOS = existe, «actualizar existentes» está encendido y el archivo trae los mismos datos (no se toca). */
export type UnitsImportAction = 'CREAR' | 'ACTUALIZAR' | 'SIN_CAMBIOS' | 'OMITIR' | 'ERROR';
export const UNITS_IMPORT_ACTION_LABELS: Record<UnitsImportAction, string> = {
  CREAR: 'Se crea',
  ACTUALIZAR: 'Se actualiza',
  SIN_CAMBIOS: 'Sin cambios',
  OMITIR: 'Se omite',
  ERROR: 'Error',
};
/** Columnas cuyo cambio en una unidad existente exige `confirmPriceChanges` (la moneda cambia el precio real). */
export const UNIT_IMPORT_PRICE_FIELDS: ReadonlyArray<UnitImportField> = [
  'priceList',
  'priceSale',
  'currency',
];

export interface UnitsImportPreviewRow {
  /** Fila del archivo tal como la numera Excel (las filas en blanco y los títulos arriba de la cabecera cuentan). */
  row: number;
  code: string | null;
  action: UnitsImportAction;
  /** Lo que se va a guardar, ya interpretado (no el texto crudo). */
  values: UnitsImportValues;
  /** Valores actuales de la unidad existente en las mismas columnas (antes → después); null si es nueva. */
  current: UnitsImportValues | null;
  /** Columnas cuyo valor cambia respecto a la unidad existente. */
  changes: UnitImportField[];
  /** Motivo del error (la fila entera se descarta: no se pisa nada). */
  error: string | null;
}

export interface UnitsImportPreview {
  total: number;
  toCreate: number;
  /** Existentes con algún dato distinto (las que traen lo mismo van en `unchanged`, no aquí). */
  toUpdate: number;
  /** Existentes iguales al archivo con «actualizar existentes» encendido: no se tocan. */
  unchanged: number;
  /** Existentes que se omiten porque «actualizar existentes» está apagado. */
  toSkip: number;
  errors: number;
  /** Unidades existentes cuyo precio (lista o venta) o moneda cambiaría si se confirma. */
  priceChanges: number;
  /** Columnas del archivo reconocidas y a qué campo van. */
  columns: Array<{ field: 'code' | UnitImportField; header: string }>;
  /** Cabeceras del archivo que no se reconocieron (se ignoran). */
  ignoredColumns: string[];
  rows: UnitsImportPreviewRow[];
}

export interface UnitDto {
  id: string;
  projectId: string;
  subdivisionId: string | null;
  subdivisionName: string | null;
  code: string;
  unitType: UnitType;
  typology: string | null;
  floor: number | null;
  view: string | null;
  areaTotal: string | null;
  areaBuilt: string | null;
  areaFree: string | null;
  bedrooms: number | null;
  bathrooms: string | null;
  priceList: string | null;
  priceSale: string | null;
  currency: Currency;
  commercialStatus: UnitCommercialStatus;
  isBlacklisted: boolean;
  blockReason: string | null;
  blockedAt: string | null;
  /**
   * Quién la tiene separada / comprada (si aplica). El cliente (`leadId`, `contactName`) solo viaja a quien puede
   * ver ese lead: quien gestiona la empresa, un rol de alcance amplio o el asesor dueño del lead; para los demás
   * (otro asesor, una clave API) van en null y queda el tipo de proceso y el asesor que atiende al cliente
   * (Lote 5 · H4, P17, ux-comercial-24).
   */
  holder: UnitHolder | null;
  externalIds: Record<string, unknown>;
  updatedAt: string;
}

export interface UnitHolder {
  leadId: string | null;
  contactName: string | null;
  dealType: 'SEPARACION' | 'VENTA';
  /** Asesor actual del lead (o el que registró el proceso); siempre visible. */
  advisorName: string | null;
}

export interface StockSummary {
  projectId: string;
  total: number;
  byStatus: Record<UnitCommercialStatus, number>;
  byType: Array<{ unitType: UnitType; total: number; disponibles: number }>;
  blacklisted: number;
  /**
   * Suma de precios vigentes (venta o, si no hay, lista) de las disponibles, **por moneda** (US$ primero): soles y
   * dólares nunca se suman en crudo (ux-comercial-25).
   */
  availableByCurrency: Array<{ currency: Currency; amount: number }>;
  /**
   * Solo con más de una moneda: el total convertido a US$ al tipo de cambio de la empresa (`settings.penPerUsd`),
   * marcado como estimado; null si todo el stock disponible está en una sola moneda.
   */
  availableEstimated: { currency: 'USD'; amount: number; penPerUsd: number } | null;
  /** Cantidades de dormitorios que existen en el proyecto (para el filtro). */
  bedrooms: number[];
}

export interface UnitsImportResult {
  total: number;
  created: number;
  /** Existentes que cambiaron (las iguales al archivo cuentan en `unchanged`). */
  updated: number;
  unchanged: number;
  skipped: number;
  errors: Array<{ row: number; code: string | null; message: string }>;
}

// ---------------------------------------------------------------- reporte de compradores

// ---------------------------------------------------------------- historial de separaciones y caídas

export const separationsReportQuerySchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  advisorId: z.string().uuid().optional(),
  /** Rango sobre `started_at` de la separación: fechas puras (días completos en la zona de la empresa) o instantes. */
  from: dateBoundSchema.optional(),
  to: dateBoundSchema.optional(),
});
export type SeparationsReportQuery = z.infer<typeof separationsReportQuerySchema>;

export interface SeparationsReport {
  companyId: string;
  projectId: string | null;
  advisorId: string | null;
  from: string | null;
  to: string | null;
  rows: SeparationRow[];
  summary: SeparationsSummary;
  /** true si se alcanzó el máximo de filas (`SEPARATIONS_REPORT_MAX_ROWS`); acotar el periodo. */
  truncated: boolean;
}

export const buyersReportQuerySchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  /** Fechas puras (días completos en la zona de la empresa) o instantes. */
  from: dateBoundSchema.optional(),
  to: dateBoundSchema.optional(),
  /** VENTA = solo compradores con venta; TODOS = ventas + separaciones activas. */
  scope: z.enum(['VENTA', 'TODOS']).default('TODOS'),
});
export type BuyersReportQuery = z.infer<typeof buyersReportQuerySchema>;

export interface BuyersReport extends BuyerProfile {
  companyId: string;
  projectId: string | null;
  from: string | null;
  to: string | null;
  scope: 'VENTA' | 'TODOS';
}
